import React, { useEffect, useState, useMemo } from "react";
import { Button, Card } from "react-bootstrap";
import { Formik, Form as FormikForm } from "formik";
import { withTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import "react-datepicker/dist/react-datepicker.css";
import { Container } from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import callApi from "../../helpers/CallApi";
import {
  getLoanList,
  getBankAccountList,
  createLoan,
  getLoanUsers,
  getLoanUserByAadharAndPan,
  getLoan,
  uploadFile,
  getLoanBillNo,
  downloadLoanApplication,
  downloadForm11,
} from "../../helpers/API";
import useApi from "../../Hooks/useApi";
import { toast } from "react-toastify";
import { addYears, addMonths } from "date-fns";
import { formatApiDate, isValidPanCard } from "../../helpers";
import { PLATFORM_NAME, LOAN_TYPES, COLLATERAL_TYPES } from "../../CommonData/Data/constant";

// Components
import StepIndicator from "./components/StepIndicator";
import VerificationModal from "./components/VerificationModal";
import CustomerInfoStep from "./components/CustomerInfoStep";
import GuarantorCollateralStep from "./components/GuarantorCollateralStep";
import LoanDetailsStep from "./components/LoanDetailsStep";
import ReviewStep from "./components/ReviewStep";

// Hooks
import useGuarantors from "./hooks/useGuarantors";
import useCollaterals from "./hooks/useCollaterals";

// Config
import {
  getInitialValues,
  getStepValidationSchema,
  STEP_VALIDATION_FIELDS,
} from "./formConfig";

const clearStepValidationState = (step, errors, touched, setErrors, setTouched) => {
  const fields = STEP_VALIDATION_FIELDS[step] || [];
  if (!fields.length) return;

  const newErrors = { ...errors };
  const newTouched = { ...touched };
  fields.forEach((field) => {
    delete newErrors[field];
    newTouched[field] = false;
  });
  setErrors(newErrors);
  setTouched(newTouched);
};

const scrollToFirstFormError = () => {
  requestAnimationFrame(() => {
    setTimeout(() => {
      const form = document.querySelector(".page-content .card-body form");
      const firstInvalid = form?.querySelector(".is-invalid");
      if (!firstInvalid) return;
      firstInvalid.scrollIntoView({ behavior: "smooth", block: "center" });
      if (typeof firstInvalid.focus === "function") {
        firstInvalid.focus({ preventScroll: true });
      }
    }, 100);
  });
};

const Add = ({ t }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [userDetailsEditable, setUserDetailsEditable] = useState(false);
  const operation = id
    ? window.location.pathname.includes("/view/") ? "view" : "edit"
    : "add";

  const [disabled, setDisabled] = useState(operation === "view" || operation === "edit");
  const [currentStep, setCurrentStep] = useState(1);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [existingCustomers, setExistingCustomers] = useState([]);

  // Photo upload state (Step 1 - Customer photo)
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);

  // Verification modal states
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [aadharCard, setAadharCard] = useState("");
  const [panCard, setPanCard] = useState("");
  const [matchingCustomers, setMatchingCustomers] = useState([]);
  const [selectedExistingCustomer, setSelectedExistingCustomer] = useState("");
  const [selectedCustomerFromDropdown, setSelectedCustomerFromDropdown] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationMode, setVerificationMode] = useState("manual");

  // Custom hooks for guarantors and collaterals
  const guarantorHook = useGuarantors();
  const collateralHook = useCollaterals();

  // Translated types
  const loanTypes = LOAN_TYPES.map((lt) => ({ ...lt, label: t(lt.labelKey) }));
  const collateralTypes = COLLATERAL_TYPES.map((ct) => ({ ...ct, label: t(ct.labelKey) }));

  document.title = t(`customer.add.headings.${operation}`, { PLATFORM_NAME });

  // ===== API CALLS =====

  useApi({
    callback: getBankAccountList,
    payload: {},
    dependencies: [],
    onSuccess: (res) => setBankAccounts(res?.data || []),
  });

  useApi({
    callback: getLoanList,
    payload: {},
    dependencies: [],
    onSuccess: () => { },
  });

  const initialValues = useMemo(() => getInitialValues(), []);
  const [formData, setFormData] = useState(initialValues);

  useApi({
    callback: getLoan,
    payload: [id],
    dependencies: [id],
    shouldCallApi: Boolean(id),
    onSuccess: (res) => {
      const borrower = res.borrower || {};
      const mappedData = {
        ...initialValues,
        billNo: res.billNo || "",
        loanType: res.loanType || "",
        date: res.date ? new Date(res.date) : new Date(),
        referrerBillNo: res.referrerBillNo || "",
        name: borrower.name || "",
        mobileNumber: borrower.mobile || "",
        email: borrower.email || "",
        aadharCard: borrower.aadharCard || "",
        panCard: borrower.panCard || "",
        caste: borrower.caste || "",
        occupation: borrower.occupation || "",
        company: borrower.company || "",
        address: borrower.address || "",
        DOB: borrower.DOB ? new Date(borrower.DOB) : null,
        borrowerId: res.borrowerId || "",
        photo: borrower.profile || null,
        loanAmount: res.loanAmount ? parseFloat(res.loanAmount).toString() : "",
        interestPerYear: res.interestPerYear ? parseFloat(res.interestPerYear).toString() : "",
        loanStartDate: res.loanStartDate ? new Date(res.loanStartDate) : new Date(),
        durationYears: res.durationYears !== null && res.durationYears !== undefined ? res.durationYears.toString() : "",
        durationMonths: res.durationMonths !== null && res.durationMonths !== undefined ? res.durationMonths.toString() : "",
        loanEndDate: res.loanEndDate ? new Date(res.loanEndDate) : null,
        interestType: res.interestType || "",
        monthlyInstallment: res.monthlyInstallment || false,
        firstEMIDate: res.firstEMIDate ? new Date(res.firstEMIDate) : null,
        totalInstallments: res.totalInstallments !== null && res.totalInstallments !== undefined ? res.totalInstallments.toString() : "",
        monthlyInstallmentAmount: res.monthlyInstallmentAmount ? parseFloat(res.monthlyInstallmentAmount).toString() : "",
        totalInterest: res.totalInterest ? parseFloat(res.totalInterest).toString() : "",
        totalAmountPayable: res.totalAmountPayable ? parseFloat(res.totalAmountPayable).toString() : "",
        totalEMI: res.totalEMI ? parseFloat(res.totalEMI).toString() : "",
        principalReceived: res.principalReceived ? parseFloat(res.principalReceived).toString() : "",
        processingCharge: res.processingCharge ? parseFloat(res.processingCharge).toString() : "",
        processingChargeReceivedMode: res.processingChargeReceivedMode || "",
        processingChargeBankAccount: res.processingChargeBankAccountId ? res.processingChargeBankAccountId.toString() : "",
        processingChargeTransactionNo: res.processingChargeTransactionNo || "",
        reminder: res.reminder || false,
        paymentType: res.paymentType || "cash",
        bankAccount: res.bankAccountId ? res.bankAccountId.toString() : "",
        chequeTransactionNo: res.chequeTransactionNo || "",
      };
      setFormData(mappedData);
      if (res.guarantors && Array.isArray(res.guarantors)) guarantorHook.setGuarantors(res.guarantors);
      if (res.collaterals && Array.isArray(res.collaterals)) collateralHook.setCollaterals(res.collaterals);
      if (operation !== "add") {
        setShowVerificationModal(false);
        setUserDetailsEditable(false);
      }
    },
  });

  useEffect(() => {
    generateTestNumbers();
    setDisabled(operation === "view");
    if (operation === "add") {
      setCurrentStep(1);
      guarantorHook.setGuarantors([]);
      collateralHook.setCollaterals([]);
      setFormData(initialValues);
      setShowVerificationModal(true);
      setMatchingCustomers([]);
      setSelectedExistingCustomer("");
      setSelectedCustomerFromDropdown("");
      setVerificationMode("manual");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operation, initialValues]);

  useApi({
    callback: getLoanBillNo,
    payload: {},
    dependencies: [],
    onSuccess: (res) => {
      setFormData((prev) => ({ ...prev, billNo: res?.billNumber || "" }));
    },
  });

  useEffect(() => {
    if (aadharCard && aadharCard.length === 12) {
      const matches = existingCustomers.filter((c) => c.aadharCard === aadharCard);
      setMatchingCustomers(matches);
    } else {
      setMatchingCustomers([]);
      setSelectedExistingCustomer("");
    }
  }, [aadharCard, existingCustomers]);

  useApi({
    callback: getLoanUsers,
    payload: {},
    shouldCallApi: showVerificationModal,
    dependencies: [showVerificationModal],
    onSuccess: (res) => setExistingCustomers(res || []),
  });

  // ===== HANDLERS =====

  const generateTestNumbers = () => {
    const aadharNumber = Array.from({ length: 12 }, () => Math.floor(Math.random() * 10)).join("");
    const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const panFirstPart = Array.from({ length: 5 }, () => letters[Math.floor(Math.random() * letters.length)]).join("");
    const panDigits = Array.from({ length: 4 }, () => Math.floor(Math.random() * 10)).join("");
    const panLastLetter = letters[Math.floor(Math.random() * letters.length)];
    setAadharCard(aadharNumber);
    setPanCard(`${panFirstPart}${panDigits}${panLastLetter}`);
  };

  const handleCustomerSelect = (customerId) => {
    setSelectedCustomerFromDropdown(customerId);
    const customer = existingCustomers.find((c) => c.borrowerId == customerId);
    if (customer) {
      setAadharCard(customer.aadharCard || "");
      setPanCard(customer.panCard || "");
      setSelectedExistingCustomer(customerId);
    }
  };

  const handleVerificationNext = async () => {
    try {
      if (verificationMode === "select") {
        if (!selectedCustomerFromDropdown) {
          toast.error(t("customer.add.verificationModal.errors.selectExistingCustomer"));
          return;
        }
        const customer = existingCustomers.find((c) => c.borrowerId == selectedCustomerFromDropdown);
        if (!customer) {
          toast.error(t("customer.add.verificationModal.errors.customerNotFound"));
          return;
        }
        const customerAadhar = customer.aadharCard || "";
        const customerPAN = customer.panCard || "";
        if (!customerAadhar || customerAadhar.length !== 12) {
          toast.error(t("customer.add.verificationModal.errors.invalidAadhar"));
          return;
        }
        if (customerPAN && !isValidPanCard(customerPAN)) {
          toast.error(t("customer.add.verificationModal.errors.invalidPan"));
          return;
        }
        setFormData((prev) => ({
          ...prev,
          aadharCard: customer.aadharCard,
          panCard: customer.panCard,
          DOB: customer.DOB,
          borrowerId: customer.borrowerId,
          caste: customer.caste,
          company: customer.company,
          mobileNumber: customer.mobile,
          email: customer.email || "",
          name: customer.name,
          occupation: customer.occupation,
          profile: customer.profile,
          address: customer.address,
        }));
        setIsVerifying(true);
        toast.success(t("customer.add.verificationModal.success.apiCalledSuccessfully"));
        setIsVerifying(false);
        setShowVerificationModal(false);
        return;
      }

      if (selectedCustomerFromDropdown) {
        const customer = existingCustomers.find((c) => c.borrowerId == selectedCustomerFromDropdown);
        if (customer) {
          setFormData((prev) => ({
            ...prev,
            aadharCard: customer.aadharCard,
            panCard: customer.panCard,
            DOB: customer.DOB,
            borrowerId: customer.borrowerId,
            caste: customer.caste,
            company: customer.company,
            mobileNumber: customer.mobile,
            email: customer.email || "",
            name: customer.name,
            occupation: customer.occupation,
            profile: customer.profile,
            address: customer.address,
          }));
          setShowVerificationModal(false);
          return;
        }
      }

      if (!aadharCard || aadharCard.length !== 12) {
        toast.error(t("customer.add.verificationModal.errors.aadharRequired"));
        return;
      }
      if (panCard && !isValidPanCard(panCard)) {
        toast.error(t("customer.add.verificationModal.errors.panRequired"));
        return;
      }
      if (matchingCustomers.length > 0 && !selectedExistingCustomer) {
        toast.error(t("customer.add.verificationModal.errors.selectOrDifferentAadhar"));
        return;
      }

      setIsVerifying(true);

      callApi({
        callback: getLoanUserByAadharAndPan,
        payload: [aadharCard, panCard],
        onSuccess: ({ data }) => {
          if (!data) {
            setUserDetailsEditable(true);
            setFormData((prev) => ({ ...prev, aadharCard, panCard, customerType: "new" }));
            setShowVerificationModal(false);
          } else {
            setFormData((prev) => ({
              ...prev,
              aadharCard: data.aadharCard,
              panCard: data.panCard,
              DOB: data.DOB,
              borrowerId: data.id,
              caste: data.caste,
              company: data.company,
              mobileNumber: data.mobile,
              email: data.email || "",
              name: data.name,
              occupation: data.occupation,
              profile: data.profile,
              address: data.address,
              customerType: "existing",
            }));
            setShowVerificationModal(false);
            setUserDetailsEditable(false);
          }
        },
        onError: (error) => {
          setIsVerifying(false);
          toast.error(error || t("customer.add.verificationModal.errors.verificationFailed"));
        },
      });
    } catch (error) {
      console.log("error", error);
      setIsVerifying(false);
      toast.error(error || t("customer.add.verificationModal.errors.verificationFailed"));
    } finally {
      setIsVerifying(false);
    }
  };

  const handlePhotoUpload = async (file, setFieldValue) => {
    if (!file) return;
    setPhotoUploading(true);
    setPhotoPreview(URL.createObjectURL(file));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await uploadFile(fd);
      setFieldValue("photo", res?.data?.url || res?.url || res?.data || "");
      toast.success(t("customer.add.messages.photoUploadSuccess"));
    } catch (error) {
      console.error("Photo upload error:", error);
      toast.error(t("customer.add.messages.photoUploadError"));
      setPhotoPreview(null);
      setFieldValue("photo", "");
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleNext = (stepErrors, setErrors, setTouched, touched, errors) => {
    if (currentStep >= 4) return;

    const stepFields = STEP_VALIDATION_FIELDS[currentStep] || [];

    if (currentStep === 2) {
      clearStepValidationState(3, errors, touched, setErrors, setTouched);
      setCurrentStep(3);
      return;
    }

    const errorFields = stepFields.filter((field) => Boolean(stepErrors[field]));

    if (errorFields.length === 0) {
      clearStepValidationState(currentStep + 1, errors, touched, setErrors, setTouched);
      setCurrentStep(currentStep + 1);
      return;
    }

    const newErrors = { ...errors };
    stepFields.forEach((field) => {
      if (stepErrors[field]) newErrors[field] = stepErrors[field];
      else delete newErrors[field];
    });
    setErrors(newErrors);
    setTouched({
      ...touched,
      ...Object.fromEntries(errorFields.map((key) => [key, true])),
    });
    scrollToFirstFormError();
  };

  const handlePrevious = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async (values) => {
    try {
      const getLoanEndDate = () => {
        return values.loanStartDate && values.durationYears !== "" && values.durationMonths !== ""
          ? (() => {
            let endDate = new Date(values.loanStartDate);
            if (values.durationYears) endDate = addYears(endDate, parseInt(values.durationYears) || 0);
            if (values.durationMonths) endDate = addMonths(endDate, parseInt(values.durationMonths) || 0);
            return formatApiDate(endDate);
          })()
          : null;
      };
      const loanEndDate = getLoanEndDate();
      const payload = {
        ...values,
        date: formatApiDate(values.date),
        loanStartDate: formatApiDate(values.loanStartDate),
        loanEndDate: loanEndDate || null,
        firstEMIDate: values.firstEMIDate ? formatApiDate(values.firstEMIDate) : null,
        guarantors: guarantorHook.guarantors,
        collaterals: collateralHook.collaterals,
        loanAmount: values.loanAmount && values.loanAmount !== "" ? parseFloat(values.loanAmount) : null,
        interestPerYear: values.interestPerYear && values.interestPerYear !== "" ? parseFloat(values.interestPerYear) : null,
        durationYears: values.durationYears && values.durationYears !== "" ? parseInt(values.durationYears, 10) : null,
        durationMonths: values.durationMonths && values.durationMonths !== "" ? parseInt(values.durationMonths, 10) : null,
        totalInstallments: values?.totalInstallments || null,
        monthlyInstallmentAmount: values.monthlyInstallmentAmount && values.monthlyInstallmentAmount !== "" ? parseFloat(values.monthlyInstallmentAmount) : null,
        totalInterest: values.totalInterest && values.totalInterest !== "" ? parseFloat(values.totalInterest) : null,
        totalAmountPayable: values.totalAmountPayable && values.totalAmountPayable !== "" ? parseFloat(values.totalAmountPayable) : null,
        totalEMI: values.totalEMI && values.totalEMI !== "" ? parseFloat(values.totalEMI) : null,
        principalReceived: values.principalReceived && values.principalReceived !== "" ? parseFloat(values.principalReceived) : null,
        processingCharge: values.processingCharge && values.processingCharge !== "" ? parseFloat(values.processingCharge) : null,
        processingChargeReceivedMode: values.processingChargeReceivedMode || null,
        processingChargeBankAccountId: values.processingChargeBankAccount && values.processingChargeBankAccount !== ""
          ? (typeof values.processingChargeBankAccount === "string" ? parseInt(values.processingChargeBankAccount, 10) : values.processingChargeBankAccount)
          : null,
        processingChargeTransactionNo: values.processingChargeTransactionNo || null,
        DOB: formatApiDate(new Date()),
      };

      callApi({
        callback: createLoan,
        payload: [payload],
        onSuccess: ({ message }) => {
          toast.success(message || (!id ? t("customer.add.messages.createSuccess") : t("customer.add.messages.updateSuccess")));
          setCurrentStep(1);
          guarantorHook.setGuarantors([]);
          collateralHook.setCollaterals([]);
          navigate("/customer");
        },
        onError: (error) => {
          toast.error(error || t("customer.add.messages.error"));
        },
      });
    } catch (error) {
      console.log("error", error);
    }
  };

  const downloadPdfBlob = (res, defaultFilename) => {
    const url = window.URL.createObjectURL(res);
    const disposition = res?.headers?.["content-disposition"];
    let filename = defaultFilename;
    if (disposition) {
      const match = disposition.match(/filename="(.+)"/);
      if (match?.[1]) filename = match[1];
    }
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadApplication = () => {
    callApi({
      callback: downloadLoanApplication,
      payload: [id],
      onSuccess: (res) => downloadPdfBlob(res, "loan-contract.pdf"),
      onError: (error) => toast.error(error || t("customer.add.messages.downloadError")),
    });
  };

  const handleDownloadForm11 = () => {
    callApi({
      callback: downloadForm11,
      payload: [id],
      onSuccess: (res) => downloadPdfBlob(res, "form-11.pdf"),
      onError: (error) => toast.error(error || t("customer.add.messages.downloadError")),
    });
  };

  const getHeading = () => t(`customer.add.headings.${operation}`);

  // ===== RENDER =====
  return (
    <div className="page-content">
      {/* Verification Modal */}
      <VerificationModal
        show={showVerificationModal}
        existingCustomers={existingCustomers}
        aadharCard={aadharCard}
        setAadharCard={setAadharCard}
        panCard={panCard}
        setPanCard={setPanCard}
        matchingCustomers={matchingCustomers}
        selectedExistingCustomer={selectedExistingCustomer}
        setSelectedExistingCustomer={setSelectedExistingCustomer}
        selectedCustomerFromDropdown={selectedCustomerFromDropdown}
        isVerifying={isVerifying}
        verificationMode={verificationMode}
        setVerificationMode={setVerificationMode}
        handleCustomerSelect={handleCustomerSelect}
        handleVerificationNext={handleVerificationNext}
      />

      <Container fluid={true}>
        <Breadcrumbs navigation={true} path={"/customer"} title={getHeading()} breadcrumbItem={t("customer.breadcrumbs.list.breadcrumbItem")} />
        <Card>
          <Card.Body>
            <Formik
              initialValues={formData}
              validationSchema={getStepValidationSchema(currentStep, t)}
              onSubmit={handleSubmit}
              validateOnBlur
              validateOnChange
              enableReinitialize
            >
              {({ errors, touched, values, setErrors, setTouched, validateForm }) => (
                <FormikForm>
                  <StepIndicator currentStep={currentStep} />

                  {/* Step 1: Customer Information */}
                  {currentStep === 1 && (
                    <CustomerInfoStep
                      disabled={disabled}
                      userDetailsEditable={userDetailsEditable}
                      loanTypes={loanTypes}
                      photoUploading={photoUploading}
                      photoPreview={photoPreview}
                      handlePhotoUpload={handlePhotoUpload}
                    />
                  )}

                  {/* Step 2: Guarantor/Collateral */}
                  {currentStep === 2 && (
                    <GuarantorCollateralStep
                      disabled={disabled}
                      // Guarantor props
                      guarantors={guarantorHook.guarantors}
                      showGuarantorModal={guarantorHook.showGuarantorModal}
                      editingGuarantorId={guarantorHook.editingGuarantorId}
                      guarantorForm={guarantorHook.guarantorForm}
                      guarantorPhotoUploading={guarantorHook.guarantorPhotoUploading}
                      guarantorPhotoPreview={guarantorHook.guarantorPhotoPreview}
                      guarantorSignatureUploading={guarantorHook.guarantorSignatureUploading}
                      guarantorSignaturePreview={guarantorHook.guarantorSignaturePreview}
                      handleGuarantorFormChange={guarantorHook.handleGuarantorFormChange}
                      handleAddGuarantor={guarantorHook.handleAddGuarantor}
                      handleEditGuarantor={guarantorHook.handleEditGuarantor}
                      handleRemoveGuarantor={guarantorHook.handleRemoveGuarantor}
                      handleGuarantorPhotoUpload={guarantorHook.handleGuarantorPhotoUpload}
                      handleGuarantorSignatureUpload={guarantorHook.handleGuarantorSignatureUpload}
                      closeGuarantorModal={guarantorHook.closeGuarantorModal}
                      openAddGuarantorModal={guarantorHook.openAddGuarantorModal}
                      // Collateral props
                      collaterals={collateralHook.collaterals}
                      collateralTypes={collateralTypes}
                      showCollateralModal={collateralHook.showCollateralModal}
                      editingCollateralId={collateralHook.editingCollateralId}
                      collateralForm={collateralHook.collateralForm}
                      collateralPhotoUploading={collateralHook.collateralPhotoUploading}
                      collateralPhotoPreview={collateralHook.collateralPhotoPreview}
                      handleCollateralFormChange={collateralHook.handleCollateralFormChange}
                      handleCollateralTypeChange={collateralHook.handleCollateralTypeChange}
                      handleAddCollateral={collateralHook.handleAddCollateral}
                      handleEditCollateral={collateralHook.handleEditCollateral}
                      handleRemoveCollateral={collateralHook.handleRemoveCollateral}
                      handleCollateralPhotoUpload={collateralHook.handleCollateralPhotoUpload}
                      closeCollateralModal={collateralHook.closeCollateralModal}
                      openAddCollateralModal={collateralHook.openAddCollateralModal}
                    />
                  )}

                  {/* Step 3: Loan Details */}
                  {currentStep === 3 && (
                    <LoanDetailsStep disabled={disabled} bankAccounts={bankAccounts} />
                  )}

                  {/* Step 4: Review */}
                  {currentStep === 4 && (
                    <ReviewStep
                      loanTypes={loanTypes}
                      collateralTypes={collateralTypes}
                      guarantors={guarantorHook.guarantors}
                      collaterals={collateralHook.collaterals}
                      bankAccounts={bankAccounts}
                    />
                  )}

                  {/* Navigation Buttons */}
                  <div className="mt-4 d-flex justify-content-between">
                    <Button variant="secondary" onClick={() => navigate("/customer")}>
                      {disabled ? t("customer.add.buttons.close") : t("customer.add.buttons.cancel")}
                    </Button>
                    {!disabled ? (
                      <div>
                        {currentStep > 1 && (
                          <Button variant="outline-primary" onClick={handlePrevious} className="me-2">
                            {t("customer.add.buttons.previous")}
                          </Button>
                        )}
                        {currentStep < 4 ? (
                          <Button
                            variant="primary"
                            onClick={async (e) => {
                              e.preventDefault();
                              const stepErrors = await validateForm();
                              handleNext(stepErrors, setErrors, setTouched, touched, errors);
                            }}
                          >
                            {t("customer.add.buttons.next")}
                          </Button>
                        ) : (
                          <div className="d-flex gap-2 flex-wrap justify-content-end">
                            {id && (
                              <>
                                <Button variant="outline-primary" onClick={handleDownloadForm11}>
                                  <i className="ri-file-download-line me-1" />
                                  {t("customer.add.buttons.downloadForm11")}
                                </Button>
                                <Button variant="outline-secondary" onClick={handleDownloadApplication}>
                                  <i className="ri-file-pdf-line me-1" />
                                  {t("customer.add.buttons.downloadApplication")}
                                </Button>
                              </>
                            )}
                            <Button variant="primary" type="submit">
                              {t("customer.add.buttons.save")}
                            </Button>
                          </div>
                        )}
                      </div>
                    ) : currentStep < 4 ? (
                      <div className="d-flex gap-2">
                        {currentStep > 1 && (
                          <Button variant="primary" onClick={() => setCurrentStep(currentStep - 1)}>
                            {t("customer.add.buttons.previous")}
                          </Button>
                        )}
                        <Button
                          variant="primary"
                          onClick={(e) => {
                            e.preventDefault();
                            setCurrentStep(currentStep + 1);
                          }}
                        >
                          {t("customer.add.buttons.next")}
                        </Button>
                      </div>
                    ) : (
                      <div className="d-flex gap-2">
                        <Button variant="primary" onClick={() => setCurrentStep(currentStep - 1)}>
                          {t("customer.add.buttons.previous")}
                        </Button>
                        <Button variant="outline-primary" onClick={handleDownloadForm11}>
                          <i className="ri-file-download-line me-1" />
                          {t("customer.add.buttons.downloadForm11")}
                        </Button>
                        <Button variant="outline-secondary" onClick={handleDownloadApplication}>
                          <i className="ri-file-pdf-line me-1" />
                          {t("customer.add.buttons.downloadApplication")}
                        </Button>
                      </div>
                    )}
                  </div>
                </FormikForm>
              )}
            </Formik>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default withTranslation()(Add);
